import { lazy, Suspense } from 'react';
import { MotionConfig } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import './index.css';
import './components/Navbar/Navbar.css';
import './components/Hero/Hero.css';
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider, useAuth } from "./components/Context/AuthContext";
import { LanguageProvider } from './components/Context/LanguageContext';
import { useTheme } from './components/Context/ThemeContext';
import ThemeToggle from './components/Common/ThemeToggle';
import { HelmetProvider } from 'react-helmet-async';
import { auth } from "./firebase";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Home = lazy(() => import("./pages/Home/Home"));
const Login = lazy(() => import("./pages/Authentication/Login"));
const Register = lazy(() => import("./pages/Authentication/Register"));
const VerifyEmail = lazy(() => import("./pages/Authentication/VerifyEmail"));
const Navbar = lazy(() => import("./components/Navbar/Navbar"));
const Footer = lazy(() => import("./components/Footer/Footer"));
const AboutUs = lazy(() => import("./pages/AboutUs/AboutUs"));
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const HowItWorks = lazy(() => import("./components/HowItWorks/HowItWorks"));
const FAQPage = lazy(() => import("./pages/FAQ/FAQPage"));
const LegalPage = lazy(() => import("./pages/Legal/LegalPage"));
const NotFound = lazy(() => import("./pages/NotFound/NotFound"));


function HomeWrapper() {
    const { user } = useAuth();
    if (user) return <Navigate to="/dashboard" replace />;
    return <Home />;
}

function AboutUsWrapper() {
    const { user } = useAuth();
    if (user) return <Navigate to="/dashboard" replace />;
    return <AboutUs />;
}

function FAQWrapper() {
    const { user } = useAuth();
    if (user) return <Navigate to="/dashboard" replace />;
    return <FAQPage />;
}

function AuthWrapper({ children }) {
    const { user } = useAuth();
    if (user) return <Navigate to="/dashboard" replace />;
    return children;
}

function VerifiedRoute({ children }) {
    const { user, isLoading } = useAuth();
    const location = useLocation();
    if (isLoading) return null;

    const firebaseUser = auth.currentUser;
    const isProfilePage = location.pathname === '/dashboard/profile';

    if (!user) return <Navigate to="/login" replace />;
    if (firebaseUser && !firebaseUser.emailVerified && !isProfilePage) return <Navigate to="/verify-email" replace />;

    return children;
}

// Prazan fallback za route-change suspense (ne remount-uje LoadingScreen)
function RouteFallback() {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            background: 'transparent'
        }}>
            <div className="auth-loader"></div>
        </div>
    );
}

function AppContent() {
    const { user, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'var(--background-color)'
            }}>
                <div className="auth-loader"></div>
            </div>
        );
    }

    const showLayout = !user && !['/login', '/register', '/verify-email'].includes(location.pathname);

    return (
        <>
            {showLayout && <Navbar />}
            {['/login', '/register', '/verify-email'].includes(location.pathname) && <ThemeToggle className="auth-theme-toggle" />}
            <Routes>
                <Route path="/" element={<HomeWrapper />} />
                <Route path="/login" element={<AuthWrapper><Login /></AuthWrapper>} />
                <Route path="/register" element={<AuthWrapper><Register /></AuthWrapper>} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/business" element={<HowItWorks />} />
                <Route path="/about" element={<AboutUsWrapper />} />
                <Route path="/faq" element={<FAQWrapper />} />
                <Route path="/privacy" element={<LegalPage key="privacy" doc="privacy" />} />
                <Route path="/terms" element={<LegalPage key="terms" doc="terms" />} />
                <Route path="/dashboard/*" element={
                    <VerifiedRoute>
                        <Dashboard />
                    </VerifiedRoute>
                } />
                <Route path="*" element={<NotFound />} />

            </Routes>
            {showLayout && <Footer />}
        </>
    );
}

function App() {
    const { theme } = useTheme();
    return (
    <MotionConfig reducedMotion="user">
    <HelmetProvider>
        <LanguageProvider>
            <AuthProvider>
                <Router>
                    <ScrollToTop />
                    <Suspense fallback={<RouteFallback />}>
                        <AppContent />
                    </Suspense>
                    <ToastContainer
                        position="bottom-left"
                        autoClose={3500}
                        hideProgressBar={false}
                        newestOnTop
                        closeOnClick
                        pauseOnHover
                        draggable
                        theme={theme}
                    />
                </Router>
            </AuthProvider>
        </LanguageProvider>
    </HelmetProvider>
    </MotionConfig>
);
}

export default App;
